import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    DocumentData,
    DocumentReference,
    Firestore,
    getCountFromServer,
    getDoc,
    getDocs,
    query,
    setDoc,
    where,
} from "firebase/firestore";
import UserProfile, { userProfileConverter, UserProfileData } from "@/contexts/UserProfileContext/UserProfile";
import interestFormConverter, { InterestForm, InterestFormResult } from "./interestFormConverter";

import { getFirestoreDB } from "@/firebase";
import { FormData, StudyDetails } from "../types";
import { contactStudyEvent, saveStudyEvent } from "@/utils/analytics/googleAnalytics";
import logToCollection from "@/utils/activityLogger";

export function isFormValid(formFields: FormData): {
    isValid: boolean;
    errorMessage?: string;
} {
    // Ensure at least one checkbox is selected
    if (formFields.buttonClick === "interested") {
        const atLeastOneCheckboxSelected =
            formFields.saveToList || formFields.contactSimilar || formFields.contactThisStudy;

        if (!atLeastOneCheckboxSelected) {
            return {
                isValid: false,
                errorMessage: "Please select at least one of the first 3 checkboxes.",
            };
        }
    }

    // Validate phone number
    /* (XXX) XXX-XXXX
        XXX-XXX-XXXX
        (XXX)-XXX-XXXX
        (XXX) XXX XXXX
        XXXXXXXXXX */
    const phoneRegex =
        /^(?:\+?\d{1,2}\s?)?(?:\(\d{3}\)\s?\d{3}[-\s]?\d{4}|\d{3}[-\s]?\d{3}[-\s]?\d{4}|\(\d{3}\)-\d{3}-\d{4}|\d{10})$/;
    if (!phoneRegex.test(formFields.phoneNumber)) {
        return {
            isValid: false,
            errorMessage: "Please enter a valid phone number!",
        };
    }

    // Validate email address
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formFields.emailAddress && !emailRegex.test(formFields.emailAddress)) {
        return {
            isValid: false,
            errorMessage: "Please enter a valid email address!",
        };
    }

    // Ensure required fields are filled
    if (!formFields.firstName || !formFields.phoneNumber) {
        return {
            isValid: false,
            errorMessage: "Please fill out all required fields!",
        };
    }

    return { isValid: true };
}

function formatPhoneNumber(phoneNumber: string) {
    let formattedPhoneNumber = phoneNumber.replace(/\D/g, ""); // Remove all non-numeric characters
    if (formattedPhoneNumber.length === 11) {
        formattedPhoneNumber = formattedPhoneNumber.slice(1, 11); // Limit to 10 digits
    }
    // Ensure the phone number starts with '+1'
    formattedPhoneNumber = `+1${formattedPhoneNumber}`;
    return formattedPhoneNumber;
}

/**
 * Creates the user profile document, if one does not already exist
 * @param db The firestore connection
 * @param userData The data to create the user profile with
 * @returns True, if a user profile was created. False if one already exists.
 */
async function createUserProfile(db: Firestore, userData: UserProfileData): Promise<boolean> {
    // TODO: Use the UserProfileContext convertor/object once we remove anonymous users.
    const userProfileCollectionRef = collection(db, "userProfile").withConverter(userProfileConverter);

    // Check if a user with the same UID already exists
    const docRef = doc(userProfileCollectionRef, userData.uid);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        const userProfile = new UserProfile({
            uid: userData.uid,
            firstName: userData.firstName,
            emailAddress: userData.emailAddress,
            phoneNumber: userData.phoneNumber,
            phoneVerified: false,
            isAnonymous: userData.isAnonymous,
        });
        const newDocRef = doc(userProfileCollectionRef, userData.uid);
        await setDoc(newDocRef, userProfile);
        return true;
    }
    return false;
}

export async function saveStudyToAccount(interestForm: InterestForm): Promise<void> {
    logToCollection(interestForm.uid, "saveStudy");
    const db = getFirestoreDB();
    const savedStudiesData = new InterestFormResult(interestForm);

    return setDoc(
        doc(db, "userProfile", interestForm.uid, "savedStudies", interestForm.nctID).withConverter(
            interestFormConverter
        ),
        savedStudiesData
    ).then((docRef) => {
        saveStudyEvent(interestForm.nctID);
        return docRef;
    });
}

export async function deleteStudyFromAccount(nctID: string, uid: string) {
    const db = getFirestoreDB();
    const docRef = doc(db, "userProfile", uid, "savedStudies", nctID);
    deleteDoc(docRef);
}

export async function dismissRecommendationFromAccount(nctID: string, uid: string) {
    const db = getFirestoreDB();
    const docRef = doc(db, "userProfile", uid, "trialFinderRecommendations", nctID);
    return setDoc(docRef, { dismissed: true }, { merge: true });
}

export async function contactStudyFromAccount(
    interestForm: InterestForm
): Promise<DocumentReference<InterestFormResult, DocumentData>> {
    logToCollection(interestForm.uid, "contactStudy");
    const db = getFirestoreDB();
    const studiesContactData = new InterestFormResult(interestForm);
    const contactStudyCollectionRef = collection(db, "trialFinderStudiesContact").withConverter(interestFormConverter);

    return addDoc(contactStudyCollectionRef, studiesContactData).then((docRef) => {
        contactStudyEvent(interestForm.nctID, docRef.id);
        return docRef;
    });
}

/**
 * Records that a user enrolled in a study.
 * Writes a document under userProfile/{uid}/enrolledStudies keyed by the study ID.
 */
export async function enrollStudyForUser(uid: string, nctID: string): Promise<void> {
    logToCollection(uid, "enrollStudy");
    const db = getFirestoreDB();
    const studiesRef = collection(db, "trialFinderStudy");
    const q = query(studiesRef, where("nctId", "==", nctID));
    if ((await getCountFromServer(q)).data().count < 1) {
        throw new Error("Study is not in database");
    }
    const enrolledStudiesRef = doc(db, "userProfile", uid, "enrolledStudies", nctID);
    return setDoc(enrolledStudiesRef, {
        nctID,
        enrolledAt: new Date(),
    });
}

export async function unenrollStudyForUser(uid: string, nctID: string): Promise<void> {
    const db = getFirestoreDB();
    const enrolledStudiesRef = doc(db, "userProfile", uid, "enrolledStudies", nctID);
    return deleteDoc(enrolledStudiesRef);
}

/**
 * Function that packages the form response along with user details to create a user account (if one does not already exist) and log the user's interests in the study
 * @param studyDetails
 * @param formData
 * @param uid
 * @param isAnonymous
 * @param searchLogId
 */
export async function sendFormToFirestore(
    studyDetails: StudyDetails,
    formData: FormData,
    uid: string,
    isAnonymous: boolean,
    searchLogId: string
) {
    const db = getFirestoreDB();

    const getCookieValue = (name: string): string | null => {
        const cookies = document.cookie.split("; ");
        const cookie = cookies.find((c) => c.startsWith(`${name}=`));
        return cookie ? cookie.split("=")[1] : null;
    };

    const cookieValue = getCookieValue("_ga") || "";

    const formattedPhoneNumber = formatPhoneNumber(formData.phoneNumber);
    const userData: UserProfileData = {
        uid,
        isAnonymous,
        phoneNumber: formattedPhoneNumber,
        emailAddress: formData.emailAddress,
        firstName: formData.firstName,
    };

    try {
        if (await createUserProfile(db, userData)) {
            console.info(`Profile created for user ${uid}`);
        }
    } catch (e) {
        console.error("Error creating User Profile %s: %s", uid, e);
    }

    // bookmarked data checkbox
    if (formData.saveToList || formData.buttonClick === "saveStudy") {
        const savedStudiesData = new InterestFormResult({
            uid,
            isAnonymous,
            nctID: studyDetails.nctId,
            isProxy: formData.isProxy,
            searchLogId,
            conditions: studyDetails.conditions,
            createdAt: new Date(),
            sessionId: cookieValue,
        });
        await saveStudyToAccount(savedStudiesData);
    }

    // contact this study data checkbox
    if (formData.contactThisStudy || formData.buttonClick === "contactStudy") {
        const studiesContactData = new InterestFormResult({
            uid,
            isAnonymous,
            nctID: studyDetails.nctId,
            isProxy: formData.isProxy,
            searchLogId,
            conditions: studyDetails.conditions,
            createdAt: new Date(),
            sessionId: cookieValue,
        });

        await contactStudyFromAccount(studiesContactData);
    }

    const searchLogCollectionRef = collection(db, "trialFinderSearchLog");

    let searchCondition = "";
    if (searchLogId.length > 0) {
        const searchLogQuery = query(searchLogCollectionRef, where("__name__", "==", searchLogId));
        const searchLogSnapshot = await getDocs(searchLogQuery);

        if (!searchLogSnapshot.empty) {
            const searchLog = searchLogSnapshot.docs[0];
            searchCondition = searchLog.data().condition;
        }
    }

    // contact this study data checkbox
    if (formData.contactSimilar) {
        const studiesSimilarData = new InterestFormResult({
            uid,
            isAnonymous,
            nctID: studyDetails.nctId,
            isProxy: formData.isProxy,
            searchLogId,
            searchLog: searchCondition,
            conditions: studyDetails.conditions,
            createdAt: new Date(),
            sessionId: cookieValue,
        });

        const similarStudyCollectionRef = collection(db, "trialFinderStudiesSimilar").withConverter(
            interestFormConverter
        );

        await addDoc(similarStudyCollectionRef, studiesSimilarData);
    }
}
